import React from 'react';
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import StyledEngineProvider from "@mui/material/StyledEngineProvider";
import RTL from "components/RTL";
import useSettings from "hooks/useSettings";
import { createCustomTheme } from "./theme";
import routes from "./routes";
import "./i18n";
import { ToastProvider, useToast } from "OtherReusableFunctionality/ToastContext";
import { PageTitleProvider } from 'contexts/PageTitleContext';
import { DeleteConfirmationProvider } from 'contexts/DeleteConfirmationContext';
import TokenRefreshLoader from "components/TokenRefreshLoader";
import networkErrorHandler from "./utils/networkErrorHandler"; // Fixed import path

const NetworkStatusToast = () => {
  const toast = useToast();

  React.useEffect(() => {
    const handleOnline = () => {
      toast.showSuccess('Back online - connection restored');
    };

    const handleOffline = () => {
      toast.showError('No network connection - offline mode active');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // immediate initial state
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      toast.showError('No network connection - offline mode active');
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toast]);

  return null;
};

const App = () => {
  const router = createBrowserRouter(routes());
  const { settings } = useSettings();
  const theme = createCustomTheme({
    theme: settings.theme,
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes
  });

  React.useEffect(() => {
    return () => {
      // Safely call destroy if it exists
      if (networkErrorHandler && typeof networkErrorHandler.destroy === 'function') {
        networkErrorHandler.destroy();
      }
    };
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ToastProvider>
        <NetworkStatusToast />
        <ThemeProvider theme={theme}>
          <PageTitleProvider>
            <DeleteConfirmationProvider>
              <RTL>
                <CssBaseline />
                <RouterProvider router={router} />
                <TokenRefreshLoader />
              </RTL>
            </DeleteConfirmationProvider>
          </PageTitleProvider>
        </ThemeProvider>
      </ToastProvider>
    </StyledEngineProvider>
  );
};

export default App;